import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Download, Home, RefreshCw, TriangleAlert } from 'lucide-react'
import { exportProjectFile } from '../project-file'
import type { DeckProject } from '../types'
import { downloadBlob, toFileSlug } from '../utils'

interface AppErrorBoundaryProps {
  children: ReactNode
  scope?: 'application' | 'workspace'
  getProject?: () => DeckProject | null
  onExit?: () => void
}

interface AppErrorBoundaryState {
  error: Error | null
  componentStack: string
  exportError: string
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, componentStack: '', exportError: '' }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Deckelier UI failure', error, info)
    this.setState({ componentStack: info.componentStack ?? '' })
  }

  private retry = () => this.setState({ error: null, componentStack: '', exportError: '' })

  private exportProject = async () => {
    const project = this.props.getProject?.()
    if (!project) return
    try {
      await exportProjectFile(project)
      this.setState({ exportError: '' })
    } catch (projectFileError) {
      try {
        downloadBlob(
          new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' }),
          `${toFileSlug(project.name)}-emergency-recovery.json`,
        )
        this.setState({ exportError: 'The normal project file could not be validated, so Deckelier downloaded self-contained emergency JSON instead.' })
      } catch {
        this.setState({ exportError: projectFileError instanceof Error ? projectFileError.message : 'Unable to export the current project.' })
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children
    const project = this.props.getProject?.() ?? null
    const workspace = this.props.scope === 'workspace'
    return (
      <section className={workspace ? 'workspace-crash' : 'application-crash'} role="alert">
        <div className="crash-icon"><TriangleAlert size={28} /></div>
        <span className="modal-kicker">Recoverable error</span>
        <h1>{workspace ? 'This workspace stopped unexpectedly' : 'Deckelier stopped unexpectedly'}</h1>
        <p>{workspace ? 'The rest of the project is still available. Retry this section, download the current project, or return to the project library.' : 'Your locally saved projects have not been deleted. Reload Deckelier, or download the currently open project first.'}</p>
        <div className="crash-actions">
          <button className="button primary" onClick={this.retry}><RefreshCw size={16} /> Retry</button>
          {project && <button className="button secondary" onClick={() => void this.exportProject()}><Download size={16} /> Download project</button>}
          {this.props.onExit && <button className="button secondary" onClick={this.props.onExit}><Home size={16} /> Project library</button>}
          {!workspace && <button className="button secondary" onClick={() => window.location.reload()}><RefreshCw size={16} /> Reload app</button>}
        </div>
        {this.state.exportError && <p className="crash-export-error">{this.state.exportError}</p>}
        <details className="crash-details"><summary>Technical details</summary><pre>{this.state.error.message}{this.state.componentStack}</pre></details>
      </section>
    )
  }
}
